import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import consequenceService from './consequencesService'

//set initial state
const initialState = {
    consequences: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: ''
}

//create new consequence
export const createConsequence = createAsyncThunk(
    'consequences/create', 
    async (consequenceData, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await consequenceService.createConsequence(consequenceData, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data &&
            error.response.data.message) || 
            error.message || 
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//get consequences for a bowtie
export const getConsequences = createAsyncThunk(
    'consequences/getAllForBowtie',
    async (bowtieId, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await consequenceService.getConsequences(bowtieId, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//delete consequence
export const deleteConsequence = createAsyncThunk(
    'consequences/delete',
    async (id, thunkAPI) => {
        try{
            const token = thunkAPI.getState().auth.user.token
            return await consequenceService.deleteConsequence(id, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//update consequence
export const updateConsequence = createAsyncThunk(
    'consequences/update',
    async(consequenceData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await consequenceService.updateConsequence(consequenceData, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//Consequence Slice
export const consequenceSlice = createSlice({
    name: 'consequences',
    initialState,
    reducers: {
        reset:(state) => initialState,
        // multiuser.md Phase 2c: applied from a socket broadcast, not a thunk.
        remoteConsequenceUpserted: (state, action) => {
            const incoming = action.payload
            const existing = state.consequences.find((c) => c._id === incoming._id)
            if (existing) {
                Object.assign(existing, incoming)
            } else {
                state.consequences.push(incoming)
            }
        },
        remoteConsequenceRemoved: (state, action) => {
            state.consequences = state.consequences.filter((c) => c._id !== action.payload)
        },
        // Patches from the shared bowtie:metrics broadcast (riskCalculator.js) —
        // effectiveSeverity/risk ripple to consequences whenever a sibling
        // threat/barrier changes, independent of this consequence's own save.
        remoteConsequenceMetricsUpdated: (state, action) => {
            action.payload.forEach(({ _id, effectiveSeverity, risk }) => {
                const existing = state.consequences.find((c) => c._id === _id)
                if (existing) {
                    existing.effectiveSeverity = effectiveSeverity
                    existing.risk = risk
                }
            })
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(createConsequence.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(createConsequence.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            // multiuser.md Phase 2c: guard against a race with the
            // entity:created socket broadcast for this same consequence —
            // see the matching comment in threatsSlice.js.
            if (!state.consequences.some((c) => c._id === action.payload._id)) {
                state.consequences.push(action.payload)
            }
        })
        .addCase(createConsequence.rejected, (state, action) =>{
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
        .addCase(getConsequences.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(getConsequences.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            state.consequences = action.payload
        })
        .addCase(getConsequences.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
            state.consequences = [] // ✅ Clear data on error
        })
        .addCase(deleteConsequence.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(deleteConsequence.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            state.consequences = state.consequences.filter(
                (item) => item._id !== action.payload._id)
        })
        .addCase(deleteConsequence.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
        .addCase(updateConsequence.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(updateConsequence.fulfilled, (state, action) => {
            const {_id, title, description, severity, severityValue} = action.payload
            const existingConsequence = state.consequences.find(consequence => consequence._id === _id)
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            if (existingConsequence) {
                existingConsequence.title = title
                existingConsequence.description = description
                existingConsequence.severity = severity
                existingConsequence.severityValue = severityValue
            }
        })
        .addCase(updateConsequence.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
    }
})

export const {reset, remoteConsequenceUpserted, remoteConsequenceRemoved, remoteConsequenceMetricsUpdated} = consequenceSlice.actions
export default consequenceSlice.reducer
